<template>
  <div
    v-if="isOldVersionEnabled"
    class="bg-static-info-low rounded-lg mb-16 flex items-center justify-between p-24"
  >
    <FormattedMessage :definition="translations.oldVersionTitle">
      <template #link>
        <RevLink
          :to="{
            name: ROUTE_NAMES.OPTIONS.SHIPPING,
            query: { 'old-version': 'true' },
          }"
        >
          {{ i18n(translations.oldVersionLink) }}
        </RevLink>
      </template>
    </FormattedMessage>
  </div>

  <div class="md:grid md:grid-cols-4 md:gap-24">
    <RevCard class="rounded-sm col-span-3 p-24">
      <ListingsBlock v-if="isOnboardingAndListingsNotCreated" />

      <RevTabs
        v-if="!isOnboardingAndListingsNotCreated"
        :label="i18n(translations.navigation)"
        role="navigation"
        scrollable
        variant="flexible"
      >
        <RevTabItem
          active
          :label="i18n(translations.productCategoryRulesTab)"
          role="tab"
          :to="{ name: ROUTE_NAMES.OPTIONS.SHIPPING }"
        />
      </RevTabs>
      <CategoryList v-if="!isOnboardingAndListingsNotCreated" />
    </RevCard>

    <div class="mt-24 space-y-24 md:mt-0">
      <QualityCharter />
      <BackPack v-if="isBackpackFeatureEnabled" />
    </div>

    <ModalOnboarding
      :button-label="i18n(translations.onboardingModalButtonLabel)"
      :title="i18n(translations.onboardingModalTitle)"
    >
      <template #description>
        {{ i18n(translations.onboardingModalDescription) }}
      </template>
      <template #to-do>
        <FormattedMessage :definition="translations.onboardingModalToDoSection">
          <template #listOptions>
            <ul class="ml-20 list-disc">
              <li>{{ i18n(translations.onboardingModalToDoFirstOption) }}</li>
              <li>{{ i18n(translations.onboardingModalToDoSecondOption) }}</li>
            </ul>
          </template>
        </FormattedMessage>
      </template>
      <template #done>
        {{ i18n(translations.onboardingModalDoneSection) }}
      </template>
    </ModalOnboarding>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import translations from './Shipping.translations'
import BackPack from './components/BackPack/BackPack.vue'
import CategoryList from './components/CategoryList/CategoryList.vue'
import ListingsBlock from './components/ListingsBlock/ListingsBlock.vue'
import QualityCharter from './components/QualityCharter/QualityCharter.vue'
import { useListings } from './composables/useListings'

const { isFeatureEnabled } = useBusinessFeaturesStore()
const i18n = useI18n()
const { hasAccessToNewShippingPage, onboardingCompleted } = useSellerInfoStore()
const { getListingsCount } = useListings()

const { data: listingsCount } = await getListingsCount()
const isBackpackFeatureEnabled = isFeatureEnabled('backpackEnabled')

const isOldVersionEnabled = computed(() => {
  return !hasAccessToNewShippingPage
})

const isOnboardingAndListingsNotCreated = computed(
  () => !onboardingCompleted && listingsCount.value.count === 0,
)
</script>
